import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { fetchGAAdminFiles, fetchSelectedFileDataForGAAdmin } from "@store/thunks/gaadminThunk";
import { setFileMeta } from "@store/slices/fileMetaSlice";
import { openModal } from "@store/slices/modalSlice";
import { resetShouldRefreshFiles, setSelectedAssignedFileId, setTargetLanguageId } from "@store/slices/gaAdminSlice";
import AssignTranslatorAdmin from "./AssignTranslatorAdmin";
import ViewAllAdminsInfo from "./ViewAllAdminsInfo";

const formatFileSize = (size) => {
  if (!size) return "0 B";
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
};

const getStatusStyle = (status) => {
  const styles = {
    PENDING: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Pending" },
    GS_ASSIGNED: { bg: "bg-blue-100", text: "text-blue-800", label: "GS Admin Assigned" },
    DEV_ASSIGNED: { bg: "bg-purple-100", text: "text-purple-800", label: "Dev Admin Assigned" },
    TA_ASSIGNED: { bg: "bg-indigo-100", text: "text-indigo-800", label: "Translator Admin Assigned" },
    VA_ASSIGNED: { bg: "bg-pink-100", text: "text-pink-800", label: "Validator Admin Assigned" },
    DU_ASSIGNED: { bg: "bg-cyan-100", text: "text-cyan-800", label: "Dev User Assigned" },
    UNDER_TRANSLATION: { bg: "bg-orange-100", text: "text-orange-800", label: "Under Translation" },
    UNDER_VALIDATION: { bg: "bg-lime-100", text: "text-lime-800", label: "Under Validation" },
    DEV_VALIDATED: { bg: "bg-emerald-100", text: "text-emerald-800", label: "Dev Validated" },
    TRANSLATED: { bg: "bg-teal-100", text: "text-teal-800", label: "Translated" },
    VALIDATED: { bg: "bg-sky-100", text: "text-sky-800", label: "Validated" },
    GS_REJECTED: { bg: "bg-rose-100", text: "text-rose-800", label: "GS Admin Rejected" },
    DEV_REJECTED: { bg: "bg-red-100", text: "text-red-800", label: "Dev Admin Rejected" },
    COMPLETED: { bg: "bg-green-100", text: "text-green-800", label: "Completed" },
  };
  return styles[status] || { bg: "bg-gray-100", text: "text-gray-800", label: status };
};

function FileDetailTableGAAdmin() {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [file, setFile] = useState({});
  const [assignedLanguagesMap, setAssignedLanguagesMap] = useState({});
  const { assignedFiles, selectedAssignedFolderId, shouldRefreshFiles } = useSelector((store) => store.gaAdmin);
  const [searchQuery, setSearchQuery] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });
  const { assignTranslatorAdmin, viewTranslatorAdmin } = useSelector((store) => store.modal);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchQuery(newValue);
    setPageIndex(0);
    setHasNextPage(true);
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };



  // Function to fetch files
  const fetchFiles = async () => {
    if (selectedAssignedFolderId) {
      setInitialLoading(true);
      try {
        const res = await dispatch(fetchGAAdminFiles(selectedAssignedFolderId, searchQuery, pageIndex, pageSize));
        if (Array.isArray(res)) {
          if (res.length < pageSize) {
            setHasNextPage(false);
          } else {
            setHasNextPage(true);
          }
        }
      } catch (error) {
        console.error("Error fetching files:", error);
        setError("Failed to fetch files: " + error.message);
        setInitialLoading(false);
      } finally {
        setInitialLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchFiles();
  }, [dispatch, searchQuery, selectedAssignedFolderId, pageIndex, pageSize]);


  useEffect(() => {
    const refreshFiles = async () => {
      if (selectedAssignedFolderId && shouldRefreshFiles) {
        await dispatch(fetchGAAdminFiles(selectedAssignedFolderId));
        dispatch(resetShouldRefreshFiles());
      }
    };
    refreshFiles();
  }, [shouldRefreshFiles, selectedAssignedFolderId, dispatch]);


  const handleAssignClick = async ({ row }) => {
    try {
      setLoading(true);
      const fileId = row.original.fileId;
      dispatch(setFileMeta({ fileId: fileId, fileName: row.original.fileName }));
      setFile({ id: fileId, name: row.original.fileName });
      await dispatch(fetchSelectedFileDataForGAAdmin(fileId));
      dispatch(openModal("assignTranslatorAdmin"));

    } catch (error) {
      console.error("Error assigning file:", error);
      setError("Failed to assign file: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileClick = async ({ row }) => {
    try {
      setLoading(true);
      const fileId = row.original.fileId;
      dispatch(setFileMeta({ fileId: fileId, fileName: row.original.fileName }));
      setFile({ id: fileId, name: row.original.fileName });
      dispatch(setTargetLanguageId(row.original.targetId));
      dispatch(setSelectedAssignedFileId(fileId));
      dispatch(openModal("viewTranslatorAdmin"));
    } catch (error) {
      console.error("Error viewing file:", error);
      setError("Failed to view file: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fileColumns = [
    {
      id: "serial_number",
      header: () => "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "fileName",
      header: () => "File Name",
      accessorKey: "fileName",
      cell: ({ row }) => (
        <button
          onClick={() => handleFileClick({ row })}
          className="text-[#ec7026] hover:text-[#d85d1a] focus:underline font-['Semantic/Strong']"
        >
          {row.original.fileName}
        </button>
      ),
    },
    {
      id: "totalSentences",
      header: () => "Total Sentences",
      accessorKey: "totalSentences",
      cellClassName: "text-center",
    },
    {
      id: "fileSize",
      header: () => "Size",
      accessorFn: (row) => row.original.fileSizeBytes,
      cell: ({ row }) => (
        <span className="font-['Semantic/Strong']">
          {formatFileSize(row.original.fileSizeBytes)}
        </span>
      ),
      cellClassName: "text-center",
    },
    {
      id: "uploadedAt",
      header: () => "Uploaded At",
      accessorKey: "uploadedAt",
      cell: ({ row }) => {
        const deadline = row.original?.uploadedAt || row.uploadedAt;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div>
              <div
                className={
                  isOverdue
                    ? "text-red-600"
                    : isApproaching
                      ? "text-amber-600"
                      : null
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
      cellClassName: "text-center",
    },
    {
      id: "fileStatus",
      header: () => "Status",
      accessorKey: "fileStatus",
      cell: ({ row }) => {
        const style = getStatusStyle(row.original.fileStatus);
        return (
          <span
            className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-medium ${style.bg} ${style.text}`}
          >
            {style.label}
          </span>
        );
      },
      cellClassName: "text-center",
    },
    {
      id: "assignTo",
      header: () => "Assign To",
      cell: ({ row }) => {
        const fileId = row.original.fileId;
        const assignedLanguages = assignedLanguagesMap[fileId] || [];
        const isFullyAssigned = assignedLanguages.length > 0 && row.original.status !== "GS_ASSIGNED";
        return (
          <button
            onClick={() => handleAssignClick({ row })}
            aria-label={isFullyAssigned ? `File ${row.original.fileName} is fully assigned` : `Assign ${row.original.fileName} to Translator Admin`}
            className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors duration-200 hover:cursor-pointer hover:bg-gray-300 ${isFullyAssigned
              ? 'border border-gray-200 text-gray-600 cursor-default'
              : 'border border-[#0066cc] text-[#0066cc] hover:bg-[#0066cc] hover:text-white focus:ring-2 focus:ring-[#0066cc]/50'
              }`}
          >
            {isFullyAssigned ? 'Assigned' : 'Assign'}
          </button>
        );
      },
      cellClassName: "text-center",
    },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Files..."
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      <div className="w-full p-4 sm:p-6 lg:p-8">
        {popup.isOpen && (
          <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
              <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
                {popup.type === "error" ? "Error" : "Info"}
              </h2>
              <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
                {popup.message}
              </p>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handlePopupClose}
                  className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
        {assignTranslatorAdmin && (
          <AssignTranslatorAdmin files={file} />
        )}
        {viewTranslatorAdmin && (
          <ViewAllAdminsInfo files={file} />
        )}
        {initialLoading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
            <div className="animate-pulse bg-gray-200 h-8 w-full mb-2 rounded"></div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded font-['Semantic/Strong']">
              <p>{error}</p>
              <p className="mt-2">Please try again later or contact support.</p>
            </div>
          </div>
        ) : !selectedAssignedFolderId ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-gray-500 text-sm font-['Semantic/Strong']">
              Please select a folder to view files.
            </div>
          </div>
        ) : !Array.isArray(assignedFiles[selectedAssignedFolderId]) ||
          assignedFiles[selectedAssignedFolderId].length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded font-['Semantic/Strong']">
              <p>No files found.</p>
              <p className="mt-2">
                You have not been assigned any files. Please contact the administrator if this seems incorrect.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="w-full overflow-x-auto">
              <GenericTable
                data={[...assignedFiles[selectedAssignedFolderId]].sort((a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt))}
                columns={fileColumns}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
                emptyMessage="No files available for this folder"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default FileDetailTableGAAdmin;